import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { confirmEmail, resendConfirmation } from '../api/auth';

type Status = 'confirming' | 'success' | 'failed' | 'invalid';

const EmailConfirmationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const email = searchParams.get('email') ?? '';

  const [status, setStatus] = useState<Status>(token && email ? 'confirming' : 'invalid');
  const [resendEmail, setResendEmail] = useState(email);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (status !== 'confirming') return;
    confirmEmail(token, email)
      .then(() => setStatus('success'))
      .catch(() => setStatus('failed'));
  }, []); // Run once on mount

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendError('');
    setResendLoading(true);
    try {
      await resendConfirmation(resendEmail);
      setResendSent(true);
    } catch {
      setResendError('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 text-center">

        {status === 'confirming' && (
          <p className="text-gray-600">Confirming your email address...</p>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <span className="text-green-500 text-6xl">&#10003;</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Email confirmed</h2>
            <p className="text-gray-600">Your account is now active.</p>
            <Link
              to="/login"
              className="inline-block mt-4 py-2 px-6 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Sign in
            </Link>
          </>
        )}

        {(status === 'failed' || status === 'invalid') && (
          <>
            <h2 className="text-2xl font-bold text-gray-900">Confirmation failed</h2>
            <p className="text-gray-600">
              {status === 'invalid'
                ? 'This link is missing required information.'
                : 'This link is invalid or has expired.'}
            </p>

            {!resendSent ? (
              <form onSubmit={handleResend} className="mt-6 space-y-4 text-left">
                <p className="text-sm text-gray-700 font-medium">Send a new confirmation link:</p>
                <input
                  type="email"
                  required
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Email address"
                />
                {resendError && <p className="text-sm text-red-600">{resendError}</p>}
                <button
                  type="submit"
                  disabled={resendLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {resendLoading ? 'Sending...' : 'Send new link'}
                </button>
              </form>
            ) : (
              <p className="mt-4 text-sm text-gray-600">
                If that email is registered and unconfirmed, a new link is on its way.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmationPage;
